import {
  KeyboardEvent,
  MutableRefObject,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { sortBy, uniq } from 'lodash';
import { getAllMathJSVariables } from '@shared/helpers/mathjs';
import {
  getExpressionVariableKeysWithParenthesesAfterGetAndIsFunctions,
  getNestedExpressionVariableKeys,
  getVariableValueFromDisplayKey,
  isSuggestedExpressionFunctionProperty,
} from '@shared/helpers/expression-select.helpers';
import { useSearch } from '@client/hooks/use-search.hook';
import { GET_ACTIVITY_EXPRESSION_VARIABLES } from '@shared/constants/activities.constants';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';
import { emptyStoreySummaryVariables } from '@shared/models/design/design-expression-variables.model';

// Dot (.) excluded so that dotted paths like programs.stairwells.gfa stay one segment for autocomplete
const OPERATORS_GLOBAL = /[\s+\-/*()]/g;
const OPERATORS = /[\s+\-/*()]/;
const OPERATORS_NO_PARENTHESES = /[\s+\-/*]/;

const GET_STOREY_VARIABLES = Object.keys(emptyStoreySummaryVariables);

const ACTIVITY_EXPRESSION_VARIABLES: string[] = (
  Object.values(GET_ACTIVITY_EXPRESSION_VARIABLES) as Record<string, number>[]
)
  .reduce((acc, value) => [...acc, ...Object.keys(value)], [] as string[])
  .filter((key: string) => key !== 'gfa'); // to avoid duplicates and the standard 'gfa' expression variable

const HIDDEN_VARIABLES = [
  ...['building', 'activities'],
  ...ACTIVITY_EXPRESSION_VARIABLES,
  ...GET_STOREY_VARIABLES,
];

interface ExpressionSelectUtils {
  matchingExpressions: string[];
  activeExpressionIndex: number | undefined;
  setActiveExpressionIndex: (index: number | undefined) => void;
  handlePlusMenuClick: () => void;
  chooseExpression: (expressionIndex?: number) => void;
  /** Prefix ending with `).` when completing a function property (e.g. getStorey(1).) */
  variablePreviewContext: string | undefined;
  customOnKeyDown: (
    e: KeyboardEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement
    >,
  ) => void;
  onKeyUp: (event: KeyboardEvent<HTMLDivElement>) => void;
}

const getParenthesisSubtraction = (before: number, value: string): number => {
  let end = value.length;
  for (let i = before + 1; i < value.length; i++) {
    const character = value[i];

    if (character && OPERATORS_NO_PARENTHESES.test(character)) {
      end = i;
      break;
    }
  }

  return value.substring(before + 1, end).split('(').length - 1;
};

/** Shape for fuzzy search; useSearch expects keys id, name, value, label. */
type VariableSearchItem = {
  id: string;
  name: string;
  value: string;
  label: string;
};

const toSearchItem = (variable: string): VariableSearchItem => ({
  id: variable,
  name: variable,
  value: variable,
  label: variable,
});

export const useExpressionSelect = (
  inputRef: MutableRefObject<HTMLInputElement | undefined>,
  expression: string,
  onExpression: (expr: string) => void,
  variables?: ExpressionVariables,
): ExpressionSelectUtils => {
  const [caretIndex, setCaretIndex] = useState(
    inputRef.current?.selectionEnd ?? 0,
  );
  const [activeExpressionIndex, setActiveExpressionIndex] = useState<
    number | undefined
  >(0);

  const mathJSVariables = useMemo((): string[] => getAllMathJSVariables(), []);

  const getBeforeAndAfterCaret = useCallback((): [number, number] => {
    const operatorIndices = [0];
    let match;
    OPERATORS_GLOBAL.lastIndex = 0;
    while ((match = OPERATORS_GLOBAL.exec(expression))) {
      operatorIndices.push(match.index);
    }
    // Treat "." after ")" as boundary so "getStorey(1)." yields beforeValue ending with ")." for function-property autocomplete
    for (let i = 1; i < expression.length; i++) {
      if (expression[i] === '.' && expression[i - 1] === ')') {
        operatorIndices.push(i);
      }
    }
    operatorIndices.push(expression.length);
    operatorIndices.sort((a, b) => a - b);

    const before =
      operatorIndices.toReversed().find((index) => index < caretIndex) ?? 0;
    const after =
      operatorIndices.find((index) =>
        caretIndex > 0 ? index >= caretIndex : index > caretIndex,
      ) ?? expression.length;

    return [before, after];
  }, [expression, caretIndex]);

  const getLocalValueBefore = useCallback(
    (before: number) => (before > 0 ? expression.substring(0, before + 1) : ''),
    [expression],
  );

  const getLocalValueBetween = useCallback(
    (before: number, after: number) =>
      expression.substring(before > 0 ? before + 1 : 0, after),
    [expression],
  );

  const getLocalValueAfter = useCallback(
    (after: number) => expression.substring(after),
    [expression],
  );

  const isHiddenVariable = useCallback(
    (variable: string) => {
      if (!variables) return HIDDEN_VARIABLES.includes(variable);
      if (typeof variables[variable] !== 'undefined') return false;
      if (variable.includes('.') || variable.includes('[')) {
        const value = getVariableValueFromDisplayKey(
          variables as Record<string, unknown>,
          variable,
        );
        if (value !== undefined) return false;
      }
      return HIDDEN_VARIABLES.includes(variable);
    },
    [variables],
  );

  const filterExpressionVariables = useCallback(
    (variable: string) => {
      const getActivityVariable = 'isActivity';
      const variableOnlyAvailableInsideIsActivity = 'activities';

      const [before] = getBeforeAndAfterCaret();

      const isInsideIsActivity = expression
        .substring(0, before)
        .endsWith(getActivityVariable);

      if (isInsideIsActivity) {
        return variable === variableOnlyAvailableInsideIsActivity;
      }

      const beforeValue = expression.substring(0, before + 1);

      const hasFunctionVariableEnding = beforeValue.endsWith(').');

      if (hasFunctionVariableEnding) {
        return isSuggestedExpressionFunctionProperty(beforeValue, variable);
      }

      return !isHiddenVariable(variable);
    },
    [getBeforeAndAfterCaret, isHiddenVariable, expression],
  );

  const expressionVariables = useMemo(
    (): string[] =>
      uniq([
        ...sortBy([
          ...Object.keys(variables ?? {}),
          ...ACTIVITY_EXPRESSION_VARIABLES,
          ...getNestedExpressionVariableKeys(
            (variables ?? {}) as Record<string, unknown>,
          ),
        ]),
        ...mathJSVariables,
      ]).filter(filterExpressionVariables),
    [variables, mathJSVariables, filterExpressionVariables],
  );

  const [searchFragment, setSearchFragment] = useState('');
  const [showAllVariables, setShowAllVariables] = useState(false);

  const searchItems = useMemo(
    (): VariableSearchItem[] => expressionVariables.map(toSearchItem),
    [expressionVariables],
  );

  const filteredBySearch = useSearch(searchItems, searchFragment, {
    shouldSort: true,
  });

  const emptyOrOperator =
    searchFragment === '' ||
    searchFragment === ' ' ||
    OPERATORS.test(searchFragment);

  // Numbers only valid as array indexes (e.g. getStorey(1)), not as variable name matches
  const fragmentIsNumber = /^\d+$/.test(searchFragment);

  const matchingExpressions = useMemo((): string[] => {
    if (showAllVariables) {
      return getExpressionVariableKeysWithParenthesesAfterGetAndIsFunctions(
        expressionVariables,
      );
    }
    if (emptyOrOperator) {
      // After "getStorey(N)." etc. we want to show property list even when fragment is empty
      const [before] = getBeforeAndAfterCaret();
      const beforeValue = expression.substring(0, before + 1);
      if (beforeValue.endsWith(').'))
        return getExpressionVariableKeysWithParenthesesAfterGetAndIsFunctions(
          expressionVariables,
        );
      return [];
    }
    if (fragmentIsNumber) return [];
    return getExpressionVariableKeysWithParenthesesAfterGetAndIsFunctions(
      filteredBySearch.map((item) => item.id),
    );
  }, [
    showAllVariables,
    expressionVariables,
    emptyOrOperator,
    fragmentIsNumber,
    filteredBySearch,
    getBeforeAndAfterCaret,
    expression,
  ]);

  const isPlusMenuActive =
    matchingExpressions.length === expressionVariables.length;

  const handlePlusMenuClick = useCallback(() => {
    if (isPlusMenuActive) {
      setShowAllVariables(false);
      setSearchFragment('');
    } else {
      setShowAllVariables(true);
      setSearchFragment('');
    }
  }, [isPlusMenuActive]);

  const getActiveExpressionIndex = useCallback(
    (direction: 'prev' | 'next', currentIndex: number | undefined): number => {
      if (direction === 'prev') {
        return !currentIndex
          ? matchingExpressions.length - 1
          : currentIndex - 1;
      }
      if (direction === 'next') {
        return currentIndex === undefined ||
          currentIndex === matchingExpressions.length - 1
          ? 0
          : currentIndex + 1;
      }
      return 0;
    },
    [matchingExpressions.length],
  );

  const replaceAt = useCallback(
    (before: number, after: number, expressionIndex?: number) => {
      const index =
        expressionIndex === undefined
          ? (activeExpressionIndex ?? 0)
          : expressionIndex;

      if (matchingExpressions[index]) {
        onExpression(
          `${getLocalValueBefore(before)}${
            matchingExpressions[index]
          }${getLocalValueAfter(after)}`,
        );
        setActiveExpressionIndex(0); // reset
      }
    },
    [
      getLocalValueBefore,
      getLocalValueAfter,
      onExpression,
      matchingExpressions,
      activeExpressionIndex,
    ],
  );

  const moveCaretToCorrectIndex = useCallback(
    (before: number, until?: number) => {
      setTimeout(() => {
        if (inputRef?.current) {
          const parenthesisSubtraction = getParenthesisSubtraction(
            before,
            inputRef.current.value,
          );

          if (until) {
            const selectionRangeBefore = before === 0 ? before : before + 1;

            inputRef.current.setSelectionRange(
              selectionRangeBefore + until - parenthesisSubtraction,
              selectionRangeBefore + until - parenthesisSubtraction,
              'forward',
            );

            return;
          }

          inputRef.current.setSelectionRange(
            expression.length - parenthesisSubtraction,
            expression.length - parenthesisSubtraction,
            'forward',
          );
        }
      }, 0);
    },
    [inputRef, expression],
  );

  const chooseExpression = useCallback(
    (expressionIndex?: number) => {
      const [before, after] = getBeforeAndAfterCaret();
      replaceAt(before, after, expressionIndex);
      inputRef.current?.focus();

      const matchingExpression =
        matchingExpressions[
          expressionIndex === undefined
            ? (activeExpressionIndex ?? 0)
            : expressionIndex
        ];

      moveCaretToCorrectIndex(before, matchingExpression?.length);

      setSearchFragment('');
      setShowAllVariables(false);
    },
    [
      replaceAt,
      getBeforeAndAfterCaret,
      inputRef,
      moveCaretToCorrectIndex,
      matchingExpressions,
      activeExpressionIndex,
    ],
  );

  const customOnKeyDown = useCallback(
    (
      e: KeyboardEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement
      >,
    ) => {
      if (
        matchingExpressions.length > 0 &&
        ['ArrowUp', 'ArrowDown', 'Enter', 'Tab'].includes(e.key)
      ) {
        e.preventDefault();
      }
    },
    [matchingExpressions.length],
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        chooseExpression();
        return;
      }
      if (
        (matchingExpressions.length > 0 && event.key === 'ArrowUp') ||
        (event.key === 'Tab' && event.shiftKey)
      ) {
        setActiveExpressionIndex(
          getActiveExpressionIndex('prev', activeExpressionIndex),
        );
        return;
      }
      if (
        matchingExpressions.length > 0 &&
        (event.key === 'ArrowDown' || event.key === 'Tab')
      ) {
        setActiveExpressionIndex(
          getActiveExpressionIndex('next', activeExpressionIndex),
        );
        return;
      }

      const [before, after] = getBeforeAndAfterCaret();
      const fragment = getLocalValueBetween(before, after);
      setSearchFragment(fragment);
      setShowAllVariables(false);
    },
    [
      chooseExpression,
      getActiveExpressionIndex,
      activeExpressionIndex,
      matchingExpressions.length,
      getBeforeAndAfterCaret,
      getLocalValueBetween,
    ],
  );

  useEffect(
    () => setCaretIndex(inputRef.current?.selectionEnd ?? 0),
    [inputRef, inputRef.current?.selectionEnd],
  );

  const variablePreviewContext = useMemo((): string | undefined => {
    const [before] = getBeforeAndAfterCaret();
    const beforeValue = expression.substring(0, before + 1);
    return beforeValue.endsWith(').') ? beforeValue : undefined;
  }, [expression, getBeforeAndAfterCaret]);

  return useMemo(
    () => ({
      matchingExpressions,
      activeExpressionIndex,
      setActiveExpressionIndex,
      handlePlusMenuClick,
      chooseExpression,
      variablePreviewContext,
      customOnKeyDown,
      onKeyUp,
    }),
    [
      matchingExpressions,
      activeExpressionIndex,
      setActiveExpressionIndex,
      handlePlusMenuClick,
      chooseExpression,
      variablePreviewContext,
      customOnKeyDown,
      onKeyUp,
    ],
  );
};
